/**
 * ProposalPreviewModal — the in-app "is it creating a beautiful proposal?"
 * answer. A full-screen glass modal that renders the SAME branded HTML the
 * Preview / Print path produces (via the one `renderDocumentHtml` router —
 * the design is never forked) inside a sandboxed iframe at page-like width.
 *
 * Brain-grounded body sections persisted in the doc's markdown `body`
 * (see docs/proposalBody) are parsed and passed to the renderer, so About
 * us / Why us / Next steps appear in the chosen template's typography.
 *
 * The header carries the existing Export PDF / Print actions — both open
 * the native print dialog scoped to the document iframe (Export PDF =
 * "Save as PDF" in that dialog), so the OS print flow stays fully
 * available from inside the modal. The print output path itself is
 * untouched: this component only reads the renderer.
 */

import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FileDown, Printer, X } from 'lucide-react';
import { C } from '../lib/tokens';
import { useFocusTrap } from '../lib/useFocusTrap';
import { renderDocumentHtml, getProposalTemplate } from '../lib/crm/proposalTemplates';
import { defaultLineItemsForDoc } from '../lib/crm/proposalDoc';
import { parseProposalBody } from '../lib/docs/proposalBody';
import type { Account, BusinessDoc } from '../lib/db/types';

interface Props {
  doc: BusinessDoc;
  /** The linked account (POC feeds the signature block); null when unknown. */
  account: Account | null;
  onClose: () => void;
}

export function ProposalPreviewModal({ doc, account, onClose }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  useFocusTrap(containerRef, true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The exact markup the print path renders — same router, same templates —
  // plus the doc's own Brain-drafted body sections (never invented here).
  const html = useMemo(
    () =>
      renderDocumentHtml(doc, {
        account,
        lineItems: defaultLineItemsForDoc(doc),
        bodySections: parseProposalBody(doc.body),
      }),
    [doc, account],
  );

  const template = getProposalTemplate(doc.templateId);

  /** Native print dialog scoped to the document iframe (also = Save as PDF). */
  const printFrame = () => {
    try {
      frameRef.current?.contentWindow?.print();
    } catch {
      // jsdom / blocked frames: the embedded "Print / Save PDF" button and
      // the classic Preview / Print tab flow remain available.
    }
  };

  return createPortal(
    <div
      data-menu-portal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(8, 8, 14, 0.72)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Preview — ${doc.title}`}
        tabIndex={-1}
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        {/* Header: title + template chip + the existing export/print actions */}
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 18px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(16,16,23,0.65)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="t-eyebrow">Preview</span>
            <div
              className="t-section-title"
              style={{
                margin: '2px 0 0',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {doc.title}
            </div>
          </div>
          {template && (
            <span
              className="glass-pill"
              title={template.description}
              style={{ color: C.textMuted, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <span
                aria-hidden
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 5,
                  background: `linear-gradient(135deg, ${template.accent}, ${template.accent2})`,
                }}
              />
              {template.name}
            </span>
          )}
          <button
            type="button"
            className="glass-btn"
            onClick={printFrame}
            title="Open the print dialog and choose Save as PDF"
          >
            <FileDown size={13} /> Export PDF
          </button>
          <button
            type="button"
            className="glass-btn"
            onClick={printFrame}
            title="Print via the native OS dialog"
          >
            <Printer size={13} /> Print
          </button>
          <button type="button" className="glass-btn" onClick={onClose} aria-label="Close preview">
            <X size={13} />
          </button>
        </header>

        {/* Page-like document surface — same width as the print shell. */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '20px 12px' }}>
          <div
            style={{
              maxWidth: 880,
              margin: '0 auto',
              height: '100%',
              minHeight: 480,
              borderRadius: 12,
              overflow: 'hidden',
              boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
            }}
          >
            <iframe
              ref={frameRef}
              title="Document preview"
              srcDoc={html}
              style={{
                width: '100%',
                height: '100%',
                minHeight: 480,
                border: 'none',
                background: '#101017',
                display: 'block',
              }}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
